import React from "react";
import {
  createBulkWaitlistAction,
  type CreateBulkWaitlistActionData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { throwIfFormServiceError } from "src/services/error";
import type { BulkActionType } from "../activity/labels";
import type { BulkTargets } from "../types";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction, trackFormActionError } from "./trackFormAction";
import { useBulkActionProgressToast } from "./useBulkActionProgressToast";
import { useRequireBulkTargets } from "./useRequireBulkTargets";

export type BulkWaitlistActionType = NonNullable<
  CreateBulkWaitlistActionData["body"]
>["data"]["action"];

type EnqueueBulkWaitlistArgs = {
  action: BulkWaitlistActionType;
};

function createBulkWaitlistBody(
  action: BulkWaitlistActionType,
  targets: BulkTargets
): CreateBulkWaitlistActionData["body"] {
  return { data: { ...targets, action } };
}

export function useEnqueueBulkWaitlist() {
  const toast = useAvelaToast();
  const showProgressToast = useBulkActionProgressToast();
  const { clearSelection, selectionSize } = useBulkActionTargets();
  const requireTargets = useRequireBulkTargets();

  return React.useCallback(
    async ({ action }: EnqueueBulkWaitlistArgs): Promise<boolean> => {
      try {
        const targets = await requireTargets();
        if (!targets) {
          return false;
        }

        const response = throwIfFormServiceError(
          await createBulkWaitlistAction({
            body: createBulkWaitlistBody(action, targets),
          })
        );

        const actionType: BulkActionType =
          action === "ADD_TO_WAITLIST"
            ? "ADD_TO_WAITLIST"
            : "REMOVE_FROM_WAITLIST";
        trackFormAction({
          verb: "Queued",
          actionType,
          bulk: true,
          count: selectionSize,
          async: true,
        });

        showProgressToast({
          bulkActionId: response.data.bulkActionId,
          formTemplateId: targets.formTemplateId,
          actionType,
          selectionSize,
        });
        clearSelection();
        return true;
      } catch (err) {
        trackFormActionError(err, {
          actionType:
            action === "ADD_TO_WAITLIST"
              ? "ADD_TO_WAITLIST"
              : "REMOVE_FROM_WAITLIST",
          bulk: true,
          count: selectionSize,
          async: true,
        });
        toast.error({
          title: "Oops! Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
        return false;
      }
    },
    [clearSelection, requireTargets, selectionSize, showProgressToast, toast]
  );
}
